import type { TranscriptSegment } from '@/types'

/**
 * Timecodes elsewhere in the product are whole seconds, so a cited moment can
 * sit up to half a second before the line it cites. Half a second, because
 * that is the largest error rounding to the nearest second can introduce.
 */
export const TIMECODE_TOLERANCE_SECONDS = 0.5

/**
 * Which segment is "at" this moment.
 *
 * An exact `start <= t < end` hit always wins. Without one -- a rounded
 * timecode landing just before its line, or a gap between segments -- the last
 * segment starting at or before `t + tolerance` is chosen, so the cited line is
 * found instead of the previous speaker's (or nothing). Failing that, `t` is
 * earlier than everything, and the nearest segment by distance to its span is
 * used. `endSeconds` is null for an open-ended segment (usually the last).
 */
export function findActiveSegmentId(
  segments: readonly TranscriptSegment[],
  t: number,
): string | null {
  const exact = segments.find(
    (s) => t >= s.startSeconds && t < (s.endSeconds ?? Number.POSITIVE_INFINITY),
  )
  if (exact) return exact.id

  let last: TranscriptSegment | null = null
  for (const s of segments) {
    if (s.startSeconds <= t + TIMECODE_TOLERANCE_SECONDS && (!last || s.startSeconds >= last.startSeconds)) {
      last = s
    }
  }
  if (last) return last.id

  let nearest: TranscriptSegment | null = null
  let best = Number.POSITIVE_INFINITY
  for (const s of segments) {
    const end = s.endSeconds ?? s.startSeconds
    const distance = t < s.startSeconds ? s.startSeconds - t : t - end
    if (distance < best) {
      best = distance
      nearest = s
    }
  }
  return nearest?.id ?? null
}
